//Functionality imports
import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import "@aws-amplify/ui-react/styles.css";
import { Authenticator, ThemeProvider, useTheme, View, Text, Heading, Button, useAuthenticator } from '@aws-amplify/ui-react';
import { BrowserRouter as Router, Route, Routes, Navigate, Link, useNavigate, useLocation } from 'react-router-dom';
import SidebarMenu from './components/sidebarmenu_comp/sidebarmenu';
import { LanguageProvider, useLanguage } from './i18n';
import { OrganizationProvider, useOrganization } from './contexts/OrganizationContext';
import LanguageSelector from './components/LanguageSelector/LanguageSelector';
/* import { FaceLivenessDetector } from '@aws-amplify/ui-react-liveness';
import { ToggleButtonGroup, ToggleButton } from '@aws-amplify/ui-react'; */

//Components imports
import ChatPage from './pages/ChatPage/chat';
import ContactPage from './pages/ContactPage/contact';
import FAQPage from './pages/FAQPage/faqpage';
// UserProfile functionality now integrated in SidebarMenu
// Legales públicos (fuera del login): Aviso de Privacidad y Términos del Servicio.
import LegalPage from './pages/LegalPage/LegalPage';
import GuiaPage from './pages/GuiaPage/guiapage';
//customization imports
// Logo por defecto - se reemplaza dinámicamente si el usuario tiene uno configurado
import defaultLogo from '../src/assets/img/logo-data-new.png';
// Logo de marca para la barra superior (reemplaza el texto "CLIA Analytics")
// Versión recortada (sin margen blanco arriba/abajo) para que no agrande la franja.
import cliaLogo from '../src/assets/img/clia-logo-cropped.png';
import GeoPage from './pages/GeoPage/geopage';
import MediaMonitoring from './components/SocialAnalytics/MediaMonitoring';
// Noticias (monitoreo de medios por keyword con SerpAPI) — sección propia,
// extraída de "Social Media". Reutiliza el mismo HomePage.
import HomePage from './pages/HomePage/homepage';
import PlanPage from './pages/Plan/planpage';
import AdminPage from './pages/AdminPage/adminpage';
// PowerBI imports
import ReportsPage from './pages/ReportsPage';
import { getAvailableReports } from './services/powerbiService';
// Price Alerts imports
import AlertsPage from './pages/AlertsPage/AlertsPage';
// Opportunities Dashboard
import OpportunitiesPage from './pages/OpportunitiesPage';
// Floating Chat
import FloatingChat from './components/FloatingChat/FloatingChat';
// Custom Reports
import ReportPage from './pages/ReportPage';
import ReportPrintPage from './pages/ReportPrintPage';
// Visuales (dashboards React config-driven — demo)
import VisualsPage from './pages/VisualsPage';
// Campañas Digitales (dashboard POC alimentado por reportes Excel mensuales)
import CampaignsPage from './pages/CampaignsPage';
// Integración con la API oficial de Meta (System User): tabs (API) + config admin
import CampaignsApiPage from './pages/CampaignsApiPage';
import BeneficiosPage from './pages/BeneficiosPage';
// Redes Orgánicas: Instagram + Facebook + TikTok (APIs oficiales) en una pantalla con pestañas.
import SocialOrganicPage from './pages/SocialOrganicPage';
import MetaConfigPage from './pages/MetaConfigPage';
// Tráfico Web (GA4). Integraciones vive ahora como pestaña de /admin.
import TrafficPage from './pages/TrafficPage';
// AndromedApp (dashboard operativo desde la réplica de CRM_FARMACIA en Supabase)
import CrmPage from './pages/CrmPage';
// Venta / Sell Out (venta al consumidor reportada por las cadenas retail)
import SelloutPage from './pages/SelloutPage';

// Custome Log in page
const components = {
  Header() {
    const { tokens } = useTheme();

    return (
      <View textAlign="center" padding={tokens.space.large}>
        <img src={defaultLogo} alt="Logo" className="login-logo" />
      </View>
    );
  },

  Footer() {
    const { tokens } = useTheme();
    // Enlaces legales en la pantalla de login: deben ser accesibles SIN sesión
    // (requisito de TikTok para aprobar la app, además del legal). Las rutas se
    // sirven fuera del Authenticator, ver el Router de App().
    const { language } = useLanguage();
    const es = language === 'es';

    return (
      <View textAlign="center" padding={tokens.space.large}>
        <Text color={tokens.colors.neutral[80]}>
          &copy; All Rights Reserved
        </Text>
        <Text color={tokens.colors.neutral[80]} fontSize="0.82rem" paddingTop={tokens.space.xs}>
          <Link to="/privacy" className="auth-legal-link">
            {es ? 'Aviso de Privacidad' : 'Privacy Policy'}
          </Link>
          <span className="auth-legal-sep">·</span>
          <Link to="/terms" className="auth-legal-link">
            {es ? 'Términos del Servicio' : 'Terms of Service'}
          </Link>
        </Text>
      </View>
    );
  },

  SignIn: {
    Header() {
      const { tokens } = useTheme();

      return (
        <Heading
          padding={`${tokens.space.xl} 0 0 ${tokens.space.xl}`}
          level={3}
        >
          {/*   Welcome! Sign In to start */}
        </Heading>
      );
    },
    Footer() {
      const [showMessage, setShowMessage] = useState(false);
      const handleClick = () => {
        setShowMessage(!showMessage); // Muestra el texto al hacer clic
      };
      return (
        <View textAlign="center">
          <Button
            fontWeight="normal"
            onClick={handleClick}
            size="small"
            variation="menu"
          >
            Forgot your Password?
          </Button>
          {showMessage && <p>Contacte a su administrador</p>}
        </View>
      );
    },
  },

  SignUp: {
    Header() {
      const { tokens } = useTheme();

      return (
        <Heading
          padding={`${tokens.space.xl} 0 0 ${tokens.space.xl}`}
          level={3}
        >
          Create a new account
        </Heading>
      );
    },
    Footer() {
      const { toSignIn } = useAuthenticator();

      return (
        <View textAlign="center">
          <Button
            fontWeight="normal"
            onClick={toSignIn}
            size="small"
            variation="link"
          >
            Back to Sign In
          </Button>
        </View>
      );
    },
  },
  ConfirmSignUp: {
    Header() {
      const { tokens } = useTheme();
      return (
        <Heading
          padding={`${tokens.space.xl} 0 0 ${tokens.space.xl}`}
          level={3}
        >
          Enter Information:
        </Heading>
      );
    },
    Footer() {
      return <Text>Footer Information</Text>;
    },
  },
  SetupTotp: {
    Header() {
      const { tokens } = useTheme();
      return (
        <Heading
          padding={`${tokens.space.xl} 0 0 ${tokens.space.xl}`}
          level={3}
        >
          Enter Information:
        </Heading>
      );
    },
    Footer() {
      return <Text>Footer Information</Text>;
    },
  },
  ConfirmSignIn: {
    Header() {
      const { tokens } = useTheme();
      return (
        <Heading
          padding={`${tokens.space.xl} 0 0 ${tokens.space.xl}`}
          level={3}
        >
          Enter Information:
        </Heading>
      );
    },
    Footer() {
      return <Text>Footer Information</Text>;
    },
  },
  ForgotPassword: {
    Header() {
      const { tokens } = useTheme();
      return (
        <Heading
        //padding={`${tokens.space.xl} 0 0 ${tokens.space.xl}`}
        //level={3}
        >
          Enter Information
        </Heading>
      );
    },
    Footer() {
      return <Text></Text>;
    },
  },
  ConfirmResetPassword: {
    Header() {
      const { tokens } = useTheme();
      return (
        <Heading
          padding={`${tokens.space.xl} 0 0 ${tokens.space.xl}`}
          level={3}
        >
          Enter Information:
        </Heading>
      );
    },
    Footer() {
      return <Text>Footer Information</Text>;
    },
  },
};
//Default values
const formFields = {
  signIn: {
    username: {
      placeholder: 'Enter your User',
    },
  },
  signUp: {
    password: {
      label: 'Password:',
      placeholder: 'Enter your Password:',
      isRequired: false,
      order: 1,
    },
    confirm_password: {
      label: 'Confirm Password:',
      order: 2,
    },
  },
  forceNewPassword: {
    password: {
      placeholder: 'Enter your Password:',
    },
  },
  forgotPassword: {
    username: {
      placeholder: 'Enter your email:',
    },
  },
  confirmResetPassword: {
    confirmation_code: {
      placeholder: 'Enter your Confirmation Code:',
      label: 'New Label',
      isRequired: false,
    },
    confirm_password: {
      placeholder: 'Enter your Password Please:',
    },
  },
  setupTotp: {
    QR: {
      totpIssuer: 'test issuer',
      totpUsername: 'amplify_qr_test_user',
    },
    confirmation_code: {
      label: 'New Label',
      placeholder: 'Enter your Confirmation Code:',
      isRequired: false,
    },
  },
  confirmSignIn: {
    confirmation_code: {
      label: 'New Label',
      placeholder: 'Enter your Confirmation Code:',
      isRequired: false,
    },
  },
};

/* //Custom Live Detector
const Customizationi18n = ({ onLivenessComplete }) => {
  const [language, setLanguage] = useState<string>('en');
  const [showLiveness, setShowLiveness] = useState(false);

  useEffect(() => {
    setShowLiveness(true);
  }, []);

  return (
    <View padding="large">
      <ToggleButtonGroup value={language} isExclusive onChange={(value) => setLanguage(value)}>
        <ToggleButton value="en">En</ToggleButton>
        <ToggleButton value="es">Es</ToggleButton>
      </ToggleButtonGroup>

      {showLiveness && (
        <FaceLivenessDetector
          sessionId={'sessionId'} // Aquí debes proporcionar un ID de sesión real.
          region="us-east-1"
          onAnalysisComplete={async () => {
            // Cuando el análisis de liveness se completa, llamar a onLivenessComplete
            onLivenessComplete();
          }}
          displayText={dictionary[language]}
        />
      )}
    </View>
  );
};
//Default values LiveDetector
const dictionary = {
  en: null,
  es: {
    photosensitivyWarningHeadingText: 'Advertencia de fotosensibilidad',
    photosensitivyWarningBodyText: 'Esta verificación muestra luces de colores. Tenga cuidado si es fotosensible.',
    goodFitCaptionText: 'Buen ajuste',
    tooFarCaptionText: 'Demasiado lejos',
    hintCenterFaceText: 'Centra tu cara',
    startScreenBeginCheckText: 'Comenzar a verificar',
  },
}; */

const STRANDS_API_URL = process.env.REACT_APP_STRANDS_API_URL || 'http://localhost:5001';

// La marca "CLIA Analytics" (logo de la barra superior y del agente ligero /
// FloatingChat) es específica de Cantabria. Los demás tenants usan su propio
// logo de organización (o el logo por defecto de la plataforma).
const CANTABRIA_ORG_ID = 'cantabria_134';

// Route guard: no muestra nada mientras cargan permisos, redirige si deshabilitado
const ProtectedRoute = ({ featureKey, userFeatures, featuresLoaded, fallback = "/", children }) => {
  if (!featuresLoaded) return null;
  if (userFeatures[featureKey] === false) {
    return <Navigate to={fallback} replace />;
  }
  return children;
};

// Override de marca del tenant DEMO (white-label). Solo se aplica cuando el usuario
// es demo (userFeatures.isDemo). Renombra la plataforma y el agente, sin tocar a los
// clientes reales. Estructura: { [lang]: { 'dotted.key': valor } } (ver LanguageContext).
const DEMO_PLATFORM_NAME = 'IA Media Analytics';
const DEMO_AGENT_NAME = 'Data Bunker Agent';
const DEMO_BRAND_OVERRIDE = {
  en: {
    appTitle: DEMO_PLATFORM_NAME,
    'menu.aiAssistant': DEMO_AGENT_NAME,
    'chat.title': DEMO_AGENT_NAME,
    'floatingChat.title': DEMO_AGENT_NAME,
    'floatingChat.welcome': "Hi! I'm **Data Bunker Agent**, your intelligent assistant. I can help you explore your campaigns, media and web metrics, and generate insights.\n\nWhat would you like to know?",
    'floatingChat.cleared': "Chat cleared. I'm **Data Bunker Agent**, your intelligent assistant. How can I help you?",
  },
  es: {
    appTitle: DEMO_PLATFORM_NAME,
    'menu.aiAssistant': DEMO_AGENT_NAME,
    'chat.title': DEMO_AGENT_NAME,
    'floatingChat.title': DEMO_AGENT_NAME,
    'floatingChat.welcome': "¡Hola! Soy **Data Bunker Agent**, tu asistente inteligente. Puedo ayudarte a explorar tus campañas, medios y métricas web, y generar insights.\n\n¿Qué te gustaría saber?",
    'floatingChat.cleared': "Chat limpiado. Soy **Data Bunker Agent**, tu asistente inteligente. ¿En qué puedo ayudarte?",
  },
  pt: {
    appTitle: DEMO_PLATFORM_NAME,
    'menu.aiAssistant': DEMO_AGENT_NAME,
    'chat.title': DEMO_AGENT_NAME,
    'floatingChat.title': DEMO_AGENT_NAME,
    'floatingChat.welcome': "Olá! Sou o **Data Bunker Agent**, seu assistente inteligente. Posso ajudá-lo a explorar suas campanhas, mídia e métricas web, e gerar insights.\n\nO que você gostaria de saber?",
    'floatingChat.cleared': "Chat limpo. Sou o **Data Bunker Agent**, seu assistente inteligente. Como posso ajudá-lo?",
  },
};

// Componente interno que maneja la aplicación autenticada con PowerBI
function AuthenticatedApp({ signOut, user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const { t, setBrandOverride } = useLanguage();
  const { updateBrandingFromUserConfig, logoUrl: orgLogoUrl } = useOrganization();

  // Determinar si estamos en la página del chat principal
  const isChatPage = location.pathname.startsWith('/chat/');

  // Vista de impresión de reportes: vive AFUERA del layout (sin sidebar,
  // header, ni floating chat) para que el PDF salga limpio y membretado.
  const isReportPrintPage = /^\/report\/[^/]+\/print\/?$/.test(location.pathname);

  // Estados para PowerBI Reports
  const [availableReports, setAvailableReports] = useState([]);
  const [currentReportId, setCurrentReportId] = useState(null);
  const [currentReportPage, setCurrentReportPage] = useState('');
  const [userFeatures, setUserFeatures] = useState(() => {
    // Leer del caché para carga instantánea
    try {
      const cached = localStorage.getItem('userFeatures');
      if (cached) return JSON.parse(cached);
    } catch (e) { /* ignorar errores de localStorage */ }
    return {
      chatAgent: false,
      powerbiReports: false,
      mediaMonitoring: false,
      priceAlerts: false,
      opportunities: false,
      fileUpload: false,
      geoAnalysis: false,
      webSearch: false,
      dataExport: false,
    };
  });
  const [featuresLoaded, setFeaturesLoaded] = useState(() => {
    try { return !!localStorage.getItem('userFeatures'); } catch (e) { return false; }
  });
  // La marca CLIA (barra superior + logo del agente ligero) solo aplica a
  // Cantabria. Se cachea para no mostrar el logo por defecto un instante antes
  // de que cargue la config en usuarios de Cantabria.
  const [isCantabria, setIsCantabria] = useState(() => {
    try { return localStorage.getItem('isCantabria') === 'true'; } catch (e) { return false; }
  });
  const [userOrganization, setUserOrganization] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [lastRefreshDate, setLastRefreshDate] = useState(null);

  const toggleSidebar = () => {
    setIsSidebarVisible(!isSidebarVisible);
  };

  // Cargar configuración del usuario y actualizar branding
  useEffect(() => {
    const loadUserConfig = async () => {
      if (!user?.username) return;

      try {
        const response = await fetch(
          `${STRANDS_API_URL}/api/user/config?username=${encodeURIComponent(user.username)}`
        );
        if (response.ok) {
          const data = await response.json();
          if (data.success !== false) {
            // Actualizar branding con los datos del usuario
            updateBrandingFromUserConfig(data);
            // ¿Es el tenant de Cantabria? Solo ellos ven la marca CLIA.
            const orgId = data.organization?.id || data.organization?.name;
            const cantabria = orgId === CANTABRIA_ORG_ID;
            setIsCantabria(cantabria);
            try { localStorage.setItem('isCantabria', String(cantabria)); } catch (e) { /* ignore */ }
            // Demo (white-label): la pestaña del navegador usa el nombre de la
            // plataforma ("IA Media Analytics"), no el del cliente. Se setea después
            // del branding para que gane sobre document.title = page_title.
            if (data.isDemo) { document.title = DEMO_PLATFORM_NAME; }
            // Cargar features/permisos de modulos del usuario
            if (data.features) {
              setUserFeatures(prev => {
                const updated = { ...prev, ...data.features };
                try { localStorage.setItem('userFeatures', JSON.stringify(updated)); } catch (e) { /* ignore */ }
                return updated;
              });
              setFeaturesLoaded(true);
              console.log('[App] Features cargadas desde user config:', data.features);
            }
            console.log('[App] Branding actualizado desde user config');
          }
        }
      } catch (error) {
        console.error('[App] Error cargando user config:', error);
        setFeaturesLoaded(true); // Marcar como cargado aun con error para no bloquear UI
      }
    };

    loadUserConfig();
  }, [user?.username, updateBrandingFromUserConfig]);

  // Marca white-label del tenant demo: renombra plataforma ("IA Media Analytics")
  // y agente ("Data Bunker Agent") SOLO para el usuario demo; los clientes reales
  // conservan los textos por defecto.
  useEffect(() => {
    setBrandOverride(userFeatures?.isDemo ? DEMO_BRAND_OVERRIDE : {});
  }, [userFeatures?.isDemo, setBrandOverride]);

  // Cargar reportes disponibles cuando el usuario se autentica
  useEffect(() => {
    const loadReports = async () => {
      if (!user?.username) return;
      // Demo (white-label): no usa PowerBI (sin fila en user_reports_config) →
      // evita el 403 de /api/powerbi-embed y el ruido en consola.
      if (userFeatures?.isDemo) { setReportsLoading(false); return; }

      try {
        setReportsLoading(true);
        console.log('[App] Cargando reportes para usuario:', user.username);

        const data = await getAvailableReports(user.username);

        if (data.reports && data.reports.length > 0) {
          setAvailableReports(data.reports);
          // Establecer el primer reporte como activo por defecto
          setCurrentReportId(data.reports[0].id);

          // Establecer la primera página del menú si existe
          if (data.reports[0].menu && data.reports[0].menu.length > 0) {
            setCurrentReportPage(data.reports[0].menu[0].name);
          }

          console.log('[App] Reportes cargados:', data.reports.length);
        }

        // Guardar información del usuario
        if (data.organization) setUserOrganization(data.organization);
        if (data.role) setUserRole(data.role);
        // NO mergear features del servicio PowerBI aquí - las features autoritativas
        // (incluyendo permisos de módulos) vienen de /api/user/config en loadUserConfig

      } catch (error) {
        console.error('[App] Error cargando reportes:', error);
      } finally {
        setReportsLoading(false);
      }
    };

    loadReports();
  }, [user?.username, userFeatures?.isDemo]);

  // Handler para cambiar de reporte (solo cambio de estado + navegación;
  // el log de acceso vive en el sidebar, donde está el click real).
  const handleReportChange = useCallback((reportId) => {
    if (reportId !== currentReportId) {
      setCurrentReportId(reportId);
      const report = availableReports.find(r => r.id === reportId);
      if (report?.menu && report.menu.length > 0) {
        setCurrentReportPage(report.menu[0].name);
      }
      navigate('/reports');
    }
  }, [currentReportId, availableReports, navigate]);

  // Handler para cambiar de página dentro del reporte
  const handleReportPageChange = useCallback((pageName) => {
    console.log('[App] Cambiando a página:', pageName);
    setCurrentReportPage(pageName);
    // Navegar a la página de reportes si no estamos ahí
    navigate('/reports');
  }, [navigate]);

  // Handler para cuando el reporte carga datos (fecha de actualización, etc.)
  const handleReportDataLoaded = useCallback((data) => {
    if (data.lastRefreshDate) {
      setLastRefreshDate(data.lastRefreshDate);
    }
    // NO mergear features del servicio PowerBI aquí - las features autoritativas
    // (incluyendo permisos de módulos) vienen de /api/user/config en loadUserConfig
  }, []);

  // Vista de impresión: sin sidebar, sin header de app, sin chat flotante.
  // Mantiene los providers (Organization, Auth, i18n) porque están en
  // niveles superiores; solo evita el chrome.
  if (isReportPrintPage) {
    return (
      <Routes>
        <Route path="/report/:id_report/print" element={<ReportPrintPage />} />
      </Routes>
    );
  }

  return (
    <div className="app-container">
      <button
        className="hamburger-button"
        onClick={toggleSidebar}
      >
        ☰
      </button>
      <SidebarMenu
        isSidebarVisible={isSidebarVisible}
        toggleSidebar={toggleSidebar}
        // Props para PowerBI reports
        availableReports={availableReports}
        currentReportId={currentReportId}
        currentReportPage={currentReportPage}
        onReportChange={handleReportChange}
        onReportPageChange={handleReportPageChange}
        userFeatures={userFeatures}
        featuresLoaded={featuresLoaded}
      />
      <div className={`content ${isSidebarVisible ? 'content-expanded' : 'content-collapsed'}`}>
        <header className="app-header">
          <div className="header-left">
            {/* La marca CLIA es exclusiva de Cantabria; el resto de tenants usa
                su propio logo de organización (o el logo por defecto). */}
            <img
              src={isCantabria ? cliaLogo : (orgLogoUrl || defaultLogo)}
              alt={t('appTitle')}
              className="app-title-logo"
              onError={(e) => { e.target.src = defaultLogo; }}
            />
          </div>
          <div className="header-right">
            <LanguageSelector />
          </div>
        </header>

        <main className="app-main">
          <Routes>
            {/* El tenant del App Review de TikTok aterriza directo en el tablero de
                TikTok orgánico: el revisor entra con sus credenciales y ve la
                integración sin tener que navegar (docs/tiktok_app_review.md). */}
            <Route path="/" element={
              featuresLoaded && userFeatures.isTiktokReview
                ? <Navigate to="/social-organic?network=tiktok" replace />
                : (
                  <ProtectedRoute featureKey="mediaMonitoring" userFeatures={userFeatures} featuresLoaded={featuresLoaded} fallback="/reports">
                    <MediaMonitoring userFeatures={userFeatures} />
                  </ProtectedRoute>
                )
            } />
            {/* Noticias: monitoreo de medios por keyword (SerpAPI). Antes era la
                vertical "Métricas Orgánicas" dentro de Social Media; ahora es su
                propia sección de menú. Mismo gate de feature (mediaMonitoring). */}
            <Route path="/noticias" element={
              <ProtectedRoute featureKey="mediaMonitoring" userFeatures={userFeatures} featuresLoaded={featuresLoaded} fallback="/reports">
                <HomePage userFeatures={userFeatures} />
              </ProtectedRoute>
            } />
            <Route path="/chat/:id_conversation" element={
              <ProtectedRoute featureKey="chatAgent" userFeatures={userFeatures} featuresLoaded={featuresLoaded}>
                <ChatPage userFeatures={userFeatures} />
              </ProtectedRoute>
            } />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/faq" element={<FAQPage />} />
            {/* /terms y /privacy se sirven ahora como rutas PÚBLICAS, fuera del
                Authenticator (ver el Router de App()). */}
            <Route path="/guia" element={<GuiaPage />} />
            <Route path="/plan" element={<PlanPage />} />
            <Route path="/geo/:id_conversation" element={<GeoPage />} />
            <Route path="/admin" element={<AdminPage userFeatures={userFeatures} />} />
            <Route
              path="/reports"
              element={
                <ProtectedRoute featureKey="powerbiReports" userFeatures={userFeatures} featuresLoaded={featuresLoaded}>
                  <ReportsPage
                    availableReports={availableReports}
                    currentReportId={currentReportId}
                    currentPage={currentReportPage}
                    onReportDataLoaded={handleReportDataLoaded}
                    onPageChange={handleReportPageChange}
                  />
                </ProtectedRoute>
              }
            />
            <Route path="/alerts" element={
              <ProtectedRoute featureKey="priceAlerts" userFeatures={userFeatures} featuresLoaded={featuresLoaded}>
                <AlertsPage />
              </ProtectedRoute>
            } />
            <Route path="/opportunities" element={
              <ProtectedRoute featureKey="opportunities" userFeatures={userFeatures} featuresLoaded={featuresLoaded}>
                <OpportunitiesPage />
              </ProtectedRoute>
            } />
            <Route path="/report/:id_report" element={<ReportPage />} />
            {/* Visuales (demo): dashboard React config-driven. Sin gate de feature
                por ahora — la config solo resuelve para usuarios demo configurados. */}
            <Route path="/visuals" element={<VisualsPage />} />
            {/* Campañas Digitales (POC): tablero alimentado por los Excel mensuales.
                En tenants demo (white-label) no hay Excel: se alimenta del fixture de
                Meta (mismo tablero, source="meta") para no mostrar datos de otro cliente. */}
            <Route path="/campaigns" element={
              userFeatures.isDemo
                ? <CampaignsPage source="all" username={user?.username} />
                : <CampaignsPage />
            } />
            {/* Tabs (API): mismos tableros pero alimentados en vivo por las APIs de
                pauta. Meta y TikTok comparten pantalla con un selector de red. */}
            <Route path="/campaigns-api" element={<CampaignsApiPage />} />
            {/* Antes era su propia pestaña; se conserva la URL para enlaces guardados. */}
            <Route path="/campaigns-tiktok" element={<Navigate to="/campaigns-api?red=tiktok" replace />} />
            {/* Beneficios Cantabria: portal de promociones (juegos, participaciones
                y premios) via el proxy /api/beneficios/*. Especifico de Cantabria:
                oculto en tenants demo (white-label), igual que AndromedApp. */}
            <Route path="/beneficios" element={<BeneficiosPage />} />
            {/* Redes Orgánicas: una pestaña por red (?network=). Las rutas viejas de
                un tablero por red redirigen a su pestaña para no romper enlaces
                guardados; /social-api es aún más vieja (IG + un KPI de Página). */}
            <Route path="/social-organic" element={<SocialOrganicPage userFeatures={userFeatures} />} />
            <Route path="/social-instagram" element={<Navigate to="/social-organic?network=instagram" replace />} />
            <Route path="/social-facebook" element={<Navigate to="/social-organic?network=facebook" replace />} />
            <Route path="/social-api" element={<Navigate to="/social-organic?network=instagram" replace />} />
            <Route path="/social-tiktok" element={<Navigate to="/social-organic?network=tiktok" replace />} />
            {/* Integración Meta: pantalla de admin para el token de System User. */}
            <Route path="/meta-config" element={<MetaConfigPage />} />
            {/* Integraciones unificadas (Meta + Google Analytics + Google Ads). */}
            <Route path="/integrations" element={<Navigate to="/admin?tab=integrations" replace />} />
            {/* Tráfico Web (Google Analytics 4). */}
            <Route path="/traffic" element={<TrafficPage />} />
            {/* AndromedApp: dashboard operativo desde la réplica de CRM_FARMACIA. */}
            <Route path="/andromedapp" element={<CrmPage />} />
            {/* Venta: sell out por cadena, tienda y SKU desde Athena. */}
            <Route path="/venta" element={<SelloutPage />} />
          </Routes>
        </main>
      </div>
      {/* Floating Chat - shown on all pages except the main chat page */}
      {!isChatPage && <FloatingChat userFeatures={userFeatures} isCantabria={isCantabria} />}
    </div>
  );
}

//App deployment
function App() {
  const { tokens } = useTheme();
  const theme = {
    name: 'Auth Theme',
    tokens: {
      components: {
        authenticator: {
          router: {
            boxShadow: `0 0 16px ${tokens.colors.overlay['10']}`,
            borderWidth: '0',
          },
          form: {
            padding: `${tokens.space.medium} ${tokens.space.xl} ${tokens.space.medium}`,
          },
        },
        button: {
          primary: {
            backgroundColor: tokens.colors.neutral['100'],
          },
          link: {
            color: tokens.colors.purple['80'],
          },
        },
        fieldcontrol: {
          _focus: {
            boxShadow: `0 0 0 2px ${tokens.colors.purple['60']}`,
          },
        },
        tabs: {
          item: {
            color: tokens.colors.neutral['80'],
            _active: {
              borderColor: tokens.colors.neutral['100'],
              color: tokens.colors.purple['100'],
            },
          },
        },
      },
    },
  };

  return (
    <ThemeProvider theme={theme}>
      <LanguageProvider>
        <View>
          {/* El Router vive POR ENCIMA del Authenticator para poder servir rutas
              públicas. /privacy y /terms deben leerse sin iniciar sesión: es una
              obligación legal y además TikTok exige que ambos enlaces sean visibles
              en el sitio para aprobar la app (docs/tiktok_app_review.md).
              El resto de la aplicación sigue detrás del login, en la ruta comodín. */}
          <Router>
            <Routes>
              <Route path="/privacy" element={<LegalPage kind="privacy" />} />
              <Route path="/terms" element={<LegalPage kind="terms" />} />
              <Route path="*" element={
                <Authenticator hideSignUp formFields={formFields} components={components}>
                  {({ signOut, user }) => (
                    <OrganizationProvider>
                      <AuthenticatedApp signOut={signOut} user={user} />
                    </OrganizationProvider>
                  )}
                </Authenticator>
              } />
            </Routes>
          </Router>
        </View>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;